import type { Game, Member } from '../../types/game'
import { assetUrl } from '../../utils/assets'

interface GameEndModalProps {
  game: Game
  members: Member[]
  myColor: string | null
  onLeave: () => void
  onReturnToLobby: () => void
}

const WIN_BLURBS = [
  "The Colony is thriving! Your bees hum in perfect harmony, the hive overflows with sweet victory, and the meadow blooms brighter than ever. Through teamwork, planning, and a bit of bee-brilliance, you've secured a future buzzing with prosperity.",
  "The Colony is thriving! Nectar flows, flowers flourish, and the hive resonates with the proud buzz of success. Your careful guidance has ensured growth, harmony, and a legacy that will echo across the meadow for generations.",
  "The Colony is thriving! The hive is jam-packed with honey, the flowers are cheering, and your worker bees are doing a little victory waggle dance. You've led your colony to a sweet, sweet win!",
  "The Colony is thriving! Against shifting seasons and swirling challenges, your bees have built a haven of abundance. The hive stands strong, united, and ready to greet tomorrow with buzzing confidence.",
  "The Colony is thriving! The hive pulses with life, its chambers heavy with honey and hope. Under your guidance, the bees have carved out a stronghold of abundance against every trial the meadow hurled their way.",
  "The Colony is thriving! From dawn to dusk, the air hums with victory. Your hive stands tall, orderly, prosperous, and ready to claim its place as a beacon of strength in the wild expanse.",
  "The Colony is thriving! The Queen's chorus echoes across the combs as workers dance in celebration. What began as a fragile start has blossomed into a bastion of harmony and resilience.",
  "The Colony is thriving! The hive's golden halls shine brighter than the morning sun. Through hardship and instinct, your bees forged a legacy that will hum through the meadow for seasons to come.",
]

const LOSS_BLURBS = [
  "The Queen lost her life. The hive collapses in chaos. Your colony's struggle ends here, undone by missteps that left no room for recovery. The meadow moves on, indifferent to your failure.",
  "The Queen lost her life. With her fall, the hive's fate is sealed, starvation, disorder, and ruin follow swiftly. This was not a defeat born of chance, but of choices that could not stand against the season's harshness.",
  "The Queen lost her life. In her absence, the colony fractures and dies, a stark reminder that nature shows no mercy. The meadow claims what cannot survive, and this time, your hive was not strong enough.",
  "The Queen lost her life. The hive spirals into collapse, its workers scattered and its future erased. The world beyond pays no heed; only the lesson remains: in the wild, weakness is final.",
  "The Queen lost her life. Without her command, the hive collapses in chaos. Your colony's struggle ends here, undone by missteps that left no room for recovery. The meadow moves on, indifferent to your failure.",
  "The Queen lost her life. With her fall, the hive's fate is sealed, starvation, disorder, and ruin follow swiftly. This was not a defeat born of chance, but of choices that could not stand against the season's harshness.",
  "The Queen lost her life. In her absence, the colony fractures and dies, a stark reminder that nature shows no mercy. The meadow claims what cannot survive, and this time, your hive was not strong enough.",
  "The Queen lost her life. The hive spirals into collapse, its workers scattered and its future erased. The world beyond pays no heed; only the lesson remains: in the wild, weakness is final.",
]

export function GameEndModal({ game, members, myColor, onLeave, onReturnToLobby }: GameEndModalProps) {
  const winner = game.winner ?? [...game.players].sort((a, b) => b.n_favor - a.n_favor)[0]
  const winnerMember = winner ? members.find((member) => member.color === winner.color) : null
  const isWinner = game.state === 'won' && winner?.color === myColor
  const heroImage = isWinner ? assetUrl('images', 'win.png') : assetUrl('images', 'loss.png')
  const winnerText = game.state === 'won' ? `${winnerMember?.alias ?? winner?.color ?? 'Player'} wins!` : 'Defeat'
  const winnerStyle = game.state === 'won'
    ? { background: `var(--${winner?.color})` }
    : { background: 'var(--black)', color: 'var(--white)' }
  const description = game.state === 'won'
    ? WIN_BLURBS[(game.current_turn + game.current_year) % WIN_BLURBS.length]
    : LOSS_BLURBS[(game.current_turn + game.current_year) % LOSS_BLURBS.length]

  return (
    <div className="game-end-modal-root">
      <div className="game-end-overlay" onClick={onLeave} />
      <div className="game-end-container">
        <div className="game-end-winner-container">
          <div className="game-end-winner">
            <div className="game-end-winner-content" style={winnerStyle}>
              {winnerText}
            </div>
          </div>
        </div>
        <div className="game-end-description">{description}</div>
        <div className="game-end tableau">
          <div className="game-end-image desktop-shell-view">
            <img src={heroImage} alt="" />
            <div className="lobby-start game-end-back-to-lobby" onClick={onReturnToLobby}>
              Back to Lobby
            </div>
          </div>
          <div className="game-end-mobile-shell mobile-shell-view">
            <div className="mobile-modal-shell game-end-mobile-card">
              <div className="mobile-modal-header">
                <div className="mobile-modal-title">{winnerText}</div>
              </div>
              <div className="mobile-modal-hero game-end-mobile-hero">
                <img src={heroImage} alt="" />
              </div>
              <div className="mobile-modal-footer">
                <div className="lobby-start game-end-back-to-lobby" onClick={onReturnToLobby}>
                  Back to Lobby
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
